/**
 * Lightweight Web Audio cue helpers. Unlike `@capacitor/haptics` these
 * work in any browser/WebView immediately — no native plugin sync, no
 * Xcode rebuild — so they're the safe fallback channel for feedback
 * (vault errors, payment confirmation) until the iOS native build picks
 * up the haptics plugin.
 *
 * Each helper lazily constructs a single shared AudioContext and is a
 * no-op if the browser can't service Web Audio (private mode, ancient
 * Android WebView, etc).
 */

let ctx = null;
function getCtx() {
  if (ctx) return ctx;
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    ctx = new C();
    return ctx;
  } catch {
    return null;
  }
}

// iOS Safari suspends the AudioContext until a user gesture, so resume
// it on first use. After that single resume() call the same context can
// produce audio for the lifetime of the page.
async function ensureRunning() {
  const c = getCtx();
  if (!c) return null;
  if (c.state === "suspended") {
    try {
      await c.resume();
    } catch {
      /* nothing else we can do */
    }
  }
  return c;
}

function tone({ frequency, duration = 0.12, type = "sine", gain = 0.18 }) {
  ensureRunning().then((c) => {
    if (!c) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.value = frequency;
    g.gain.value = gain;
    // Quick attack + release so it sounds like a tap, not a beep.
    const now = c.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(gain, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(g);
    g.connect(c.destination);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  });
}

/** Soft confirmation chime — vault unlocked, payment confirmed. */
export function soundSuccess() {
  // Two-note ascending chime (E5 → A5) feels like "open".
  tone({ frequency: 660, duration: 0.1, type: "sine", gain: 0.16 });
  setTimeout(() => tone({ frequency: 880, duration: 0.14, type: "sine", gain: 0.18 }), 90);
}

/** Sharp denial buzz — wrong PIN, payment failed. */
export function soundError() {
  // Square-wave low-then-lower buzz feels like a denied access tone.
  tone({ frequency: 240, duration: 0.16, type: "square", gain: 0.14 });
  setTimeout(() => tone({ frequency: 180, duration: 0.18, type: "square", gain: 0.14 }), 120);
}

/** Faint key-press tick. Quiet enough to layer with rapid PIN entry. */
export function soundTick() {
  tone({ frequency: 1500, duration: 0.04, type: "triangle", gain: 0.07 });
}
